import React, { useMemo } from 'react'

import { GameId, ProductV2 } from '@common'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/buttons/Button'
import Grid from '@/components/Grid'
import { Arrow } from '@/components/Icons/Arrow'
import ProductCard, { EmptyProductCard } from '@/features/products/ProductCard'
import { Game } from '@/fixtures/games'
import { shortSlugToGameNameKey } from '@/localization/utils'
import { sortProducts } from '@/utils/products'
import { useBreakpoints } from '@/utils/useBreakpoints'

import { useGames } from '../common/useGames'

import styles from './ProductList.module.scss'

const GameModule = ({ game, products }: { game: Game; products: ProductV2[] | undefined }) => {
  const t = useTranslations()
  const { from } = useBreakpoints()

  const maxProductsToDisplay = useMemo(() => {
    if (from('laptop')) {
      return 4
    }
    return 3
  }, [from])

  if (!products || products.length === 0) {
    return null
  }

  const sortedProducts = products.sort(sortProducts)

  return (
    <div id={game.slug} key={game.slug} className={styles.GameModule}>
      <div className={styles.ProductContainer}>
        <div className={styles.ProductContainerHeader}>
          <p>{t(shortSlugToGameNameKey(game.shortSlug))}</p>
          {sortedProducts.length > maxProductsToDisplay && (
            <Button href={`/game/${game.slug}`} type="Tertiary" additionalClasses={styles.ViewMore}>
              <div className={styles.ViewMoreContent}>
                <p>{t('lan_prod_button')}</p>
                <Arrow className={styles.ViewMoreArrow} direction="Right" />
              </div>
            </Button>
          )}
        </div>
        <Grid singleRow additionalClasses={styles.Grid}>
          {sortedProducts.map((product) => (
            <ProductCard type={'Normal'} game={game} key={product.id} product={product} />
          ))}
          <EmptyProductCard game={game} className={styles.Empty} />
          <EmptyProductCard game={game} className={styles.Empty} />
          <EmptyProductCard game={game} className={styles.Empty} />
        </Grid>
      </div>
    </div>
  )
}

const ProductList: React.FC<{ products: ProductV2[] }> = ({ products }) => {
  const games = useGames()

  const productsPerGame = products.reduce<Partial<Record<GameId, ProductV2[]>>>((acc, cur) => {
    const prev = (acc[cur.game] || []).concat(cur)
    acc[cur.game] = prev
    return acc
  }, {})

  return (
    <section className={styles.ProductList}>
      {games.map((game) => (
        <GameModule key={game.slug} game={game} products={productsPerGame[game.slug]} />
      ))}
    </section>
  )
}

export default ProductList
